import { Button } from '@mui/material'
import { ContainerPositionType, JourneyCategory } from '@planning/app/api'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { VesselTallyViewStore } from '@planning/rt-stores/tallyman/VesselTallyViewStore'
import { useTranslate } from '@tolgee/react'
import { Header, Loader, StepperInfo } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, useState } from 'react'
import { CustomAlert } from './Components/CustomAlert'
import { NavigationStackBackButton } from './Components/HeaderComponents/NavigationStackBackButton'

export interface TallyEquipmentSelectionOrderData {
  id: number
  hasDamage?: boolean
  containerNumber?: string
}

export interface ITallymanOperationsUIStore {
  selectedEquipmentId?: number
  selectedContainerPosition?: ContainerPositionType | 'YardReservationFailed' | 'TrailerParkingLot'
  canCheckInContainer: boolean
  setCanCheckInContainer(value: boolean): void
  setSelectedOrderId(id?: number): void
}

export interface TallyEquipmentSelectorParams {
  vesselVisitId?: number
  craneName?: string
  showAlert: boolean
  journeyCategory: JourneyCategory
}

interface IProps {
  orderData: TallyEquipmentSelectionOrderData
  tallymanViewStore: VesselTallyViewStore
  tallymanOperationsUIStore: ITallymanOperationsUIStore
  onGetWorkInstruction: (orderId: number) => Promise<boolean>
  onSelectEquipment: (
    orderData: TallyEquipmentSelectionOrderData,
    order: IOrderItem | undefined,
  ) => void
  renderEquipmentSelector: (params: TallyEquipmentSelectorParams) => ReactNode
}

export const EquipmentSelection: FC<IProps> = observer(
  ({
    orderData,
    tallymanViewStore,
    tallymanOperationsUIStore,
    onGetWorkInstruction,
    onSelectEquipment,
    renderEquipmentSelector,
  }) => {
    const { t } = useTranslate()
    const { selectedOrder: order } = tallymanViewStore.searchContainerStore
    const [showAlert, setShowAlert] = useState<boolean>(false)

    const { loading } = useAsyncFetch(async () => {
      const isWorkInstructionPresent = await onGetWorkInstruction(orderData.id)
      if (isWorkInstructionPresent) {
        tallymanOperationsUIStore.setCanCheckInContainer(true)
      }
    }, [])

    const onCheckContainer = () => {
      if (
        !tallymanOperationsUIStore.selectedEquipmentId &&
        !tallymanOperationsUIStore.selectedContainerPosition
      ) {
        setShowAlert(true)
        return
      }

      onSelectEquipment(orderData, order)
      tallymanOperationsUIStore.setSelectedOrderId()
    }

    const buttonText = () => {
      if (tallymanOperationsUIStore.selectedContainerPosition === ContainerPositionType.Vessel) {
        return t('next', 'Next')
      }

      return `${t('check', 'Check')} ${
        tallymanViewStore.selectedOperation === 'Discharge' ? t('in', 'in') : t('out', 'out')
      }`
    }

    const getTotalStepCount = () => {
      if (tallymanOperationsUIStore.selectedContainerPosition === ContainerPositionType.Vessel) {
        return orderData.hasDamage ? 4 : 3
      }
      return orderData.hasDamage ? 3 : 2
    }

    return (
      <>
        <Header
          leftRenderOption={<NavigationStackBackButton />}
          title={order?.data.containerNumber || orderData?.containerNumber}
          rightRenderOption={
            <Button
              data-cy='check-container-btn'
              variant='contained'
              onClick={onCheckContainer}
              disabled={!tallymanOperationsUIStore.canCheckInContainer}
              sx={{ width: '15%', minWidth: '110px' }}
            >
              {buttonText()}
            </Button>
          }
        />
        <StepperInfo
          title={t('selectDestination', 'Select destination')}
          steps={`${orderData.hasDamage ? 3 : 2} ${t('of', 'of')} ${getTotalStepCount()}`}
        ></StepperInfo>
        <Loader show={loading} />
        {!loading && tallymanOperationsUIStore.canCheckInContainer ? (
          renderEquipmentSelector({
            vesselVisitId: order?.data.carrierVisitId ?? undefined,
            craneName: tallymanViewStore.selectedCrane?.name,
            showAlert,
            journeyCategory: order?.journeyCategory ?? JourneyCategory.None,
          })
        ) : (
          <CustomAlert
            severity='warning'
            message={t(
              'workInstructionIsBeingCreatedPleaseWait',
              'Work instruction is being created. Please wait…',
            )}
          ></CustomAlert>
        )}
      </>
    )
  },
)
